import React, { useState, useEffect } from 'react';
import { registerUserAccount, fetchBankCodes } from '../api/userApi';

export default function HostAccountVerify() {
  const [banks, setBanks] = useState<{ code: string, name: string }[]>([]);
  const [loadingBanks, setLoadingBanks] = useState(true);
  const [bank, setBank] = useState('');
  const [accountNumber, setAccountNumber] = useState('');
  const [holder, setHolder] = useState('');
  const [phone, setPhone] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [agrees, setAgrees] = useState({ isOwner: false, unrefundable: false, privacy: false });
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        setLoadingBanks(true);
        const list = await fetchBankCodes();
        setBanks(list);
      } catch {
        setBanks([]);
      } finally {
        setLoadingBanks(false);
      }
    })();
  }, []);

  // isValidAccount 검사 수정: 숫자 10~15자리만 허용(하이픈 무시)
  const isValidAccount = (() => {
    const num = accountNumber.replace(/-/g,'');
    return /^\d{10,15}$/.test(num);
  })();
  const allInputValid = bank && accountNumber && holder && isValidAccount;
  const allChecked = agrees.isOwner && agrees.unrefundable && agrees.privacy;

  const handleAgreeChange = (key: string) => setAgrees(prev => ({ ...prev, [key]: !prev[key as keyof typeof agrees] }));

  const openModal = (e: React.FormEvent) => {
    e.preventDefault();
    if (allInputValid) setModalOpen(true);
  };

  const handleConfirm = async () => {
    setSubmitted(true);
    try {
      await registerUserAccount({
        bank_code: bank,
        account_number: accountNumber,
        account_holder_name: holder
      });
      setModalOpen(false);
      setSubmitted(false);
      alert('계좌 정보가 성공적으로 등록되었습니다!');
    } catch (err: any) {
      setSubmitted(false);
      alert(err?.message || '계좌 등록에 실패했습니다.');
    }
  };

  const handleEdit = () => {
    setModalOpen(false);
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center">
      <form className="max-w-md w-full bg-white p-8 rounded-lg shadow" onSubmit={openModal}>
        <h2 className="text-xl font-bold mb-6 text-center">정산 계좌 인증</h2>
        <div className="mb-4">
          <label className="block font-semibold mb-1">은행명</label>
          {loadingBanks ? (
            <div className="text-gray-400 text-sm px-2 py-2">은행 리스트 로딩중...</div>
          ) : (
            <select
              className="w-full border rounded px-3 py-2"
              value={bank}
              onChange={e => setBank(e.target.value)}
              required>
              <option value="">은행 선택</option>
              {banks.map(b => (
                <option key={b.code} value={b.code}>{b.name}</option>
              ))}
            </select>
          )}
        </div>
        <div className="mb-4">
          <label className="block font-semibold mb-1">계좌번호</label>
          <input className="w-full border rounded px-3 py-2" value={accountNumber} onChange={e => setAccountNumber(e.target.value.replace(/[^0-9-]/g, ''))} required placeholder="예: 123-456-789012" />
          {!isValidAccount && accountNumber && <div className="text-xs text-red-500">계좌번호 형식을 확인하세요.</div>}
        </div>
        <div className="mb-4">
          <label className="block font-semibold mb-1">예금주명</label>
          <input className="w-full border rounded px-3 py-2" value={holder} onChange={e => setHolder(e.target.value)} required placeholder="본인 이름" />
        </div>
        <button type="submit" disabled={!allInputValid} className={`w-full mt-4 py-3 rounded text-white font-lg transition ${allInputValid ? 'bg-blue-600 hover:bg-blue-700' : 'bg-gray-300 cursor-not-allowed'}`}>계좌 정보 확인</button>
      </form>

      {/* 모달 */}
      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
          <div className="bg-white p-8 rounded-lg shadow-md max-w-md w-full mx-auto">
            <h3 className="text-xl font-bold mb-3 text-center text-blue-600">계좌정보 확인</h3>
            <div className="mb-4 p-3 rounded border bg-gray-50 text-sm text-gray-900">
              <b>계좌번호:</b> {accountNumber} <br />
              <b>은행:</b> {bank && (banks.find(bk => bk.code === bank)?.name || bank)} <br />
              <b>예금주:</b> {holder} <br />
            </div>
            <div className="mb-3 space-y-2 text-sm">
              <label className="flex gap-2 items-start"><input type="checkbox" className="mt-1" checked={agrees.isOwner} onChange={() => handleAgreeChange('isOwner')} />본 계좌는 <b>본인 명의</b>입니다.</label>
              <label className="flex gap-2 items-start"><input type="checkbox" className="mt-1" checked={agrees.unrefundable} onChange={() => handleAgreeChange('unrefundable')} />오기재에 따른 오송금은 <b>환불 불가</b>에 동의합니다.</label>
              <label className="flex gap-2 items-start"><input type="checkbox" className="mt-1" checked={agrees.privacy} onChange={() => handleAgreeChange('privacy')} />개인정보처리 및 정산 약관에 동의합니다.</label>
            </div>
            <div className="flex gap-3 mt-4">
              <button onClick={handleEdit} className="flex-1 py-2 rounded bg-gray-200 hover:bg-gray-300 font-medium">수정</button>
              <button onClick={handleConfirm} disabled={!allChecked || submitted} className={`flex-1 py-2 rounded text-white font-semibold ${allChecked ? 'bg-blue-600 hover:bg-blue-700' : 'bg-gray-300 cursor-not-allowed'}`}>
                {submitted ? '처리 중...' : '확인'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
